import React, { useState } from 'react';
import { Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Slider } from '../ui/slider';
import { Badge } from '../ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../ui/sheet';

const categories = [
  { value: 'sports', label: 'Sports' },
  { value: 'arts', label: 'Arts & Crafts' },
  { value: 'stem', label: 'STEM' },
  { value: 'tutoring', label: 'Tutoring' },
  { value: 'music', label: 'Music' },
  { value: 'dance', label: 'Dance' },
  { value: 'martial_arts', label: 'Martial Arts' },
  { value: 'aquatics', label: 'Aquatics' },
  { value: 'camp', label: 'Camp' },
];

const daysOfWeek = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const timeSlots = [
  { value: 'morning', label: 'Morning (6am-12pm)' },
  { value: 'afternoon', label: 'Afternoon (12pm-5pm)' },
  { value: 'evening', label: 'Evening (5pm-9pm)' },
];

const skillLevels = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'all_levels', label: 'All Levels' },
];

const indoorOutdoorOptions = [
  { value: 'indoor', label: 'Indoor' },
  { value: 'outdoor', label: 'Outdoor' },
  { value: 'both', label: 'Both' },
];

export interface SearchFiltersState {
  categories: string[];
  age: number | null;
  days: number[];
  timeSlots: string[];
  priceMin: number;
  priceMax: number;
  skillLevels: string[];
  indoorOutdoor: string[];
  verifiedOnly: boolean;
  bookableOnly: boolean;
  neighborhoods: string[];
}

export const defaultFilters: SearchFiltersState = {
  categories: [],
  age: null,
  days: [],
  timeSlots: [],
  priceMin: 0,
  priceMax: 500,
  skillLevels: [],
  indoorOutdoor: [],
  verifiedOnly: false,
  bookableOnly: false,
  neighborhoods: [],
};

interface SearchFiltersProps {
  filters: SearchFiltersState;
  onFiltersChange: (filters: SearchFiltersState) => void;
  neighborhoods?: string[];
}

const FilterSection = ({ title, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button className="flex w-full items-center justify-between py-2 text-sm font-medium hover:bg-accent rounded px-2 -mx-2">
          {title}
          {isOpen ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-2 pb-4">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
};

const FiltersContent = ({
  filters,
  onFiltersChange,
  neighborhoods = [],
}: SearchFiltersProps) => {
  const toggleArrayFilter = (key, value) => {
    const arr = filters[key];
    const newArr = arr.includes(value)
      ? arr.filter(v => v !== value)
      : [...arr, value];
    onFiltersChange({ ...filters, [key]: newArr });
  };

  const activeFiltersCount =
    filters.categories.length +
    filters.days.length +
    filters.timeSlots.length +
    filters.skillLevels.length +
    filters.indoorOutdoor.length +
    filters.neighborhoods.length +
    (filters.verifiedOnly ? 1 : 0) +
    (filters.bookableOnly ? 1 : 0) +
    (filters.age !== null ? 1 : 0) +
    (filters.priceMin > 0 || filters.priceMax < 500 ? 1 : 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Filters</h3>
        {activeFiltersCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onFiltersChange(defaultFilters)}
          >
            <X className="h-4 w-4 mr-1" />
            Clear all ({activeFiltersCount})
          </Button>
        )}
      </div>

      <div className="divide-y">
        <FilterSection title="Category">
          <div className="space-y-2">
            {categories.map(cat => (
              <label
                key={cat.value}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Checkbox
                  checked={filters.categories.includes(cat.value)}
                  onCheckedChange={() =>
                    toggleArrayFilter('categories', cat.value)
                  }
                />
                <span className="text-sm">{cat.label}</span>
              </label>
            ))}
          </div>
        </FilterSection>

        <FilterSection title="Child's Age">
          <div className="pt-2">
            <Input
              type="number"
              min={0}
              max={18}
              placeholder="Enter age (0-18)"
              value={filters.age !== null ? filters.age.toString() : ''}
              onChange={e =>
                onFiltersChange({
                  ...filters,
                  age: e.target.value ? parseInt(e.target.value, 10) : null,
                })
              }
              className="w-full"
            />
          </div>
        </FilterSection>

        <FilterSection title="Days">
          <div className="space-y-2">
            {daysOfWeek.map(day => (
              <label
                key={day.value}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Checkbox
                  checked={filters.days.includes(day.value)}
                  onCheckedChange={() => toggleArrayFilter('days', day.value)}
                />
                <span className="text-sm">{day.label}</span>
              </label>
            ))}
          </div>
        </FilterSection>

        <FilterSection title="Time of Day">
          <div className="space-y-2">
            {timeSlots.map(slot => (
              <label
                key={slot.value}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Checkbox
                  checked={filters.timeSlots.includes(slot.value)}
                  onCheckedChange={() =>
                    toggleArrayFilter('timeSlots', slot.value)
                  }
                />
                <span className="text-sm">{slot.label}</span>
              </label>
            ))}
          </div>
        </FilterSection>

        <FilterSection title="Price Range">
          <div className="space-y-4 pt-2">
            <Slider
              value={[filters.priceMin, filters.priceMax]}
              onValueChange={([min, max]) =>
                onFiltersChange({ ...filters, priceMin: min, priceMax: max })
              }
              min={0}
              max={500}
              step={10}
            />
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>${filters.priceMin}</span>
              <span>${filters.priceMax}+</span>
            </div>
          </div>
        </FilterSection>

        {neighborhoods.length > 0 && (
          <FilterSection title="Neighbourhood">
            <div className="space-y-2">
              {neighborhoods.map(n => (
                <label
                  key={n}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Checkbox
                    checked={filters.neighborhoods.includes(n)}
                    onCheckedChange={() =>
                      toggleArrayFilter('neighborhoods', n)
                    }
                  />
                  <span className="text-sm">{n}</span>
                </label>
              ))}
            </div>
          </FilterSection>
        )}

        <FilterSection title="Skill Level" defaultOpen={false}>
          <div className="space-y-2">
            {skillLevels.map(level => (
              <label
                key={level.value}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Checkbox
                  checked={filters.skillLevels.includes(level.value)}
                  onCheckedChange={() =>
                    toggleArrayFilter('skillLevels', level.value)
                  }
                />
                <span className="text-sm">{level.label}</span>
              </label>
            ))}
          </div>
        </FilterSection>

        <FilterSection title="Environment" defaultOpen={false}>
          <div className="space-y-2">
            {indoorOutdoorOptions.map(opt => (
              <label
                key={opt.value}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Checkbox
                  checked={filters.indoorOutdoor.includes(opt.value)}
                  onCheckedChange={() =>
                    toggleArrayFilter('indoorOutdoor', opt.value)
                  }
                />
                <span className="text-sm">{opt.label}</span>
              </label>
            ))}
          </div>
        </FilterSection>

        <FilterSection title="Trust & Booking">
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={filters.verifiedOnly}
                onCheckedChange={checked =>
                  onFiltersChange({ ...filters, verifiedOnly: !!checked })
                }
              />
              <span className="text-sm">Verified organisations only</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={filters.bookableOnly}
                onCheckedChange={checked =>
                  onFiltersChange({ ...filters, bookableOnly: !!checked })
                }
              />
              <span className="text-sm">Bookable on platform</span>
            </label>
          </div>
        </FilterSection>
      </div>
    </div>
  );
};

export const SearchFiltersDesktop = (props: SearchFiltersProps) => {
  return (
    <div className="hidden lg:block w-72 shrink-0">
      <div className="sticky top-20 p-4 border rounded-md bg-card">
        <FiltersContent {...props} />
      </div>
    </div>
  );
};

export const SearchFiltersMobile = (props: SearchFiltersProps) => {
  const activeCount =
    props.filters.categories.length +
    props.filters.days.length +
    props.filters.timeSlots.length +
    (props.filters.verifiedOnly ? 1 : 0) +
    (props.filters.bookableOnly ? 1 : 0);

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" className="lg:hidden gap-2">
          <Filter className="h-4 w-4" />
          Filters
          {activeCount > 0 && (
            <Badge variant="secondary" className="ml-1">
              {activeCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[320px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Filters</SheetTitle>
        </SheetHeader>
        <div className="mt-4">
          <FiltersContent {...props} />
        </div>
      </SheetContent>
    </Sheet>
  );
};
